import React from "react";
import { motion } from "framer-motion";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const { FiCheck, FiX, FiMinus } = FiIcons;

/**
 * GreenVsConventional – Detailed comparison section
 * Provides a rich comparison table + editorial prose comparing green gas turbines
 * to conventional gas turbines, batteries, and other alternatives.
 * Targets comparison search intent and adds unique content depth.
 */
export default function GreenVsConventional() {
  const rows = [
    {
      metric: "CO₂ Emissions (kg/MWh)",
      green: "0–200",
      conventional: "350–500",
      greenBetter: true,
    },
    {
      metric: "NOx Emissions (ppm)",
      green: "<9 (DLN)",
      conventional: "25–50",
      greenBetter: true,
    },
    {
      metric: "Fuel Flexibility",
      green: "H₂, RNG, biogas, NH₃, natural gas",
      conventional: "Natural gas, diesel, fuel oil",
      greenBetter: true,
    },
    {
      metric: "Simple-Cycle Efficiency",
      green: "38–44%",
      conventional: "33–40%",
      greenBetter: true,
    },
    {
      metric: "Combined-Cycle Efficiency",
      green: "60–64%",
      conventional: "55–62%",
      greenBetter: true,
    },
    {
      metric: "Start-Up Time",
      green: "<10 minutes",
      conventional: "10–30 minutes",
      greenBetter: true,
    },
    {
      metric: "Carbon Price Exposure",
      green: "Low to zero",
      conventional: "High and rising",
      greenBetter: true,
    },
    {
      metric: "Capital Cost Premium",
      green: "+10–15% vs. conventional",
      conventional: "Baseline",
      greenBetter: false,
    },
    {
      metric: "Design Life",
      green: "25–30 years",
      conventional: "25–30 years",
      greenBetter: null,
    },
    {
      metric: "Stranded-Asset Risk",
      green: "Low (fuel-flexible)",
      conventional: "High (fossil lock-in)",
      greenBetter: true,
    },
  ];

  return (
    <section id="green-vs-conventional" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-secondary-900 mb-6">
            Green Gas Turbines vs. Conventional Gas Turbines
          </h2>
          <p className="text-lg md:text-xl text-secondary-600 leading-relaxed">
            How do hydrogen-ready green gas turbines stack up against traditional fossil-fired
            models? The comparison below covers emissions, efficiency, economics, and long-term
            asset value.
          </p>
        </motion.div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="overflow-x-auto"
        >
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="text-left py-4 px-6 bg-gray-50 text-secondary-700 font-semibold text-sm uppercase tracking-wider rounded-tl-xl">
                  Performance Metric
                </th>
                <th className="text-center py-4 px-6 bg-primary-50 text-primary-800 font-semibold text-sm uppercase tracking-wider">
                  Green Gas Turbine
                </th>
                <th className="text-center py-4 px-6 bg-gray-50 text-secondary-700 font-semibold text-sm uppercase tracking-wider rounded-tr-xl">
                  Conventional Turbine
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr
                  key={row.metric}
                  className={`border-b border-gray-100 ${
                    idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                  }`}
                >
                  <td className="py-4 px-6 text-secondary-800 font-medium text-sm">
                    {row.metric}
                  </td>
                  <td className="py-4 px-6 text-center">
                    <div className="flex items-center justify-center gap-2">
                      {row.greenBetter === true && (
                        <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-600 flex-shrink-0" />
                      )}
                      {row.greenBetter === false && (
                        <SafeIcon icon={FiMinus} className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      )}
                      {row.greenBetter === null && (
                        <SafeIcon icon={FiMinus} className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      )}
                      <span className="text-sm text-secondary-700">{row.green}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <div className="flex items-center justify-center gap-2">
                      {row.greenBetter === true && (
                        <SafeIcon icon={FiX} className="h-4 w-4 text-red-400 flex-shrink-0" />
                      )}
                      {row.greenBetter === false && (
                        <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-600 flex-shrink-0" />
                      )}
                      {row.greenBetter === null && (
                        <SafeIcon icon={FiMinus} className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      )}
                      <span className="text-sm text-secondary-700">{row.conventional}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* Editorial analysis */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 max-w-4xl mx-auto"
        >
          <div className="prose prose-lg prose-secondary max-w-none text-secondary-700 leading-relaxed space-y-6">
            <h3 className="text-2xl font-bold text-secondary-900">
              Emissions: The Decisive Advantage
            </h3>
            <p>
              The most significant difference between green and conventional gas turbines is their
              carbon footprint. A conventional combined-cycle gas turbine (CCGT) emits approximately{" "}
              <strong>350–400 kg CO₂ per MWh</strong>. A green gas turbine burning a 50% hydrogen
              blend cuts that figure to roughly <strong>175–200 kg/MWh</strong>, and at 100% hydrogen
              the stack CO₂ drops to <strong>zero</strong>. With carbon prices trending upward
              globally—the EU ETS averaged €85/tonne in 2024, and California's cap-and-trade programme
              exceeded $35/tonne—this emissions gap translates directly into operating-cost savings
              that compound over the 25-year life of the asset.
            </p>

            <h3 className="text-2xl font-bold text-secondary-900 mt-10">
              Green Gas Turbines vs. Battery Energy Storage
            </h3>
            <p>
              Batteries and green gas turbines are <em>complementary</em>, not competing technologies.
              Lithium-ion battery systems excel at short-duration storage (1–4 hours) and frequency
              regulation, with round-trip efficiencies of 85–90%. However, for durations beyond 4–6
              hours, battery costs escalate linearly with capacity, while a gas turbine's cost is
              dominated by the generator—not the fuel tank. A 100 MW / 8-hour battery system costs
              roughly <strong>$400–600 million</strong>; a 100 MW hydrogen-ready gas turbine with
              on-site storage costs <strong>$80–120 million</strong> and can run indefinitely as long
              as fuel is supplied.
            </p>
            <p>
              For grid planners, the optimal portfolio combines batteries for fast-response ancillary
              services with green gas turbines for multi-day reliability events—a configuration
              increasingly referred to as a <strong>"clean firm"</strong> resource stack.
            </p>

            <h3 className="text-2xl font-bold text-secondary-900 mt-10">
              The Economic Case: Total Cost of Ownership
            </h3>
            <p>
              While green gas turbines carry a 10–15% capital cost premium over conventional models,
              the total cost of ownership (TCO) over a 25-year project life is increasingly
              favourable. Key economic drivers include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Avoided carbon costs</strong> — At €85/tonne CO₂, a 50 MW CCGT saves
                $5–8 million/year by switching to a 60% hydrogen blend.
              </li>
              <li>
                <strong>Clean-energy incentives</strong> — The US IRA Section 45V credit provides up
                to $3/kg for clean hydrogen production; the EU Innovation Fund and UK CCUS cluster
                programmes offer similar support.
              </li>
              <li>
                <strong>Fuel cost trajectory</strong> — Green hydrogen costs are falling 15–20% per
                year as electrolyser manufacturing scales. Bloomberg NEF projects green H₂ will be
                cheaper than fossil gas in many markets by 2030.
              </li>
              <li>
                <strong>Asset residual value</strong> — Hydrogen-ready turbines retain higher resale
                and refinancing value because they face no stranded-asset risk.
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
